import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Cookies from 'js-cookie';
import AuthLayout from './auth/AuthLayout';
import AuthForm from './auth/AuthForm';
import OAuthButton from './OAuthButton';
import { API_BASE_URL } from '../App';
import { validateEmail, validatePassword } from '../lib/emailValidation';
import { storeReferralCode } from '../lib/referralStorage';
import './LoginPage.css';

function LoginPage({ product }) {
  const isCostco = product === 'costco';
  const [searchParams] = useSearchParams();
  const redirect = searchParams.get('redirect') || (isCostco ? '/' : '/');

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [needsVerification, setNeedsVerification] = useState(false);
  const [resendStatus, setResendStatus] = useState(null);

  const handleSubmit = async () => {
    setError('');
    setNeedsVerification(false);
    setResendStatus(null);

    const emailCheck = validateEmail(email);
    const pwCheck = validatePassword(password);
    const errors = {};
    if (!emailCheck.ok) errors.email = emailCheck.error;
    if (!pwCheck.ok) errors.password = pwCheck.error;
    setFieldErrors(errors);
    if (Object.keys(errors).length) return;

    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/sessions/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: emailCheck.normalized, password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (data.needsVerification) setNeedsVerification(true);
        setError(data.error || 'Sign in failed');
        return;
      }
      if (data.sessionToken) {
        Cookies.set('session', data.sessionToken, { path: '/' });
      }
      window.location.href = redirect;
    } catch (e) {
      setError(e.message || 'Network error');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResendStatus(null);
    const emailCheck = validateEmail(email);
    if (!emailCheck.ok) return;
    try {
      const res = await fetch(`${API_BASE_URL}/sessions/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: emailCheck.normalized }),
      });
      const data = await res.json().catch(() => ({}));
      setResendStatus({ type: 'ok', message: data.message || 'Verification email sent.' });
    } catch (e) {
      setResendStatus({ type: 'error', message: e.message || 'Could not resend email' });
    }
  };

  return (
    <AuthLayout
      brand={isCostco ? 'costco' : undefined}
      title={isCostco ? 'Sign in' : 'Welcome to Plate Chase'}
      subtitle={
        isCostco
          ? 'Sign in to track your warehouse visits across the country'
          : 'Sign in to track your license plate progress and sync across devices'
      }
      footer={
        <p>
          By signing in, you agree to our <Link to="/terms-of-service">Terms of Service</Link> and{' '}
          <Link to="/privacy-policy">Privacy Policy</Link>
        </p>
      }
    >
      <div className="login-buttons login-buttons--compact">
        <div className="login-button-group">
          <OAuthButton onBeforeOAuth={storeReferralCode} useApiRedirect={isCostco} />
        </div>
      </div>

      <div className="login-divider">
        <span>or continue with email</span>
      </div>

      <AuthForm
        fields={[
          {
            id: 'login-email',
            name: 'email',
            label: 'Email',
            type: 'email',
            value: email,
            onChange: setEmail,
            autoComplete: 'email',
            error: fieldErrors.email,
            disabled: loading,
          },
          {
            id: 'login-password',
            name: 'password',
            label: 'Password',
            type: 'password',
            value: password,
            onChange: setPassword,
            autoComplete: 'current-password',
            error: fieldErrors.password,
            disabled: loading,
          },
        ]}
        onSubmit={handleSubmit}
        submitLabel="Sign in"
        loading={loading}
        error={error}
        status={resendStatus}
      />

      {needsVerification && (
        <p className="auth-form__secondary-action">
          <button type="button" className="auth-form__link-btn" onClick={handleResend}>
            Resend verification email
          </button>
        </p>
      )}

      <p className="auth-form__links">
        <Link to="/forgot-password">Forgot password?</Link>
        <span className="auth-form__links-sep">·</span>
        <Link to={`/signup${redirect !== '/' ? `?redirect=${encodeURIComponent(redirect)}` : ''}`}>
          Create account
        </Link>
      </p>
    </AuthLayout>
  );
}

export default LoginPage;
